<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgFollowupAPI from 'dashboard/api/ggFollowup';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  operation: { type: Object, required: true },
  useTextOnly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update', 'reset', 'reselect', 'reload', 'useText']);
const { t, te, locale } = useI18n();
const request = useAbortableRequest();
const operation = ref(props.operation);
const text = ref('');
const error = ref('');
const contextChanged = ref(false);
const targetRejected = ref(false);
const POLL_INTERVAL_MS = 3000;
const WORKING_STATES = ['submitting', 'queued', 'processing', 'confirming'];
const MAX_TEXT_LENGTH = 120000;
let timer;
let disposed = false;

const working = computed(
  () =>
    WORKING_STATES.includes(operation.value.status) ||
    (operation.value.domain === 'support' &&
      operation.value.status === 'unknown')
);
const ready = computed(() => operation.value.status === 'ready');
const saved = computed(() => operation.value.status === 'saved');
const support = computed(() => operation.value.domain === 'support');
const targetLabel = computed(() =>
  t(
    support.value
      ? 'GG_FOLLOWUP.SUPPORT.TICKET_VALUE'
      : 'GG_FOLLOWUP.OPPORTUNITY_VALUE',
    {
      id: support.value
        ? operation.value.ticket_id
        : operation.value.opportunity_id,
    }
  )
);
const opportunityMissing = computed(
  () =>
    !operation.value.analysis_id &&
    (targetRejected.value ||
      operation.value.last_error_code === 'OPPORTUNITY_NOT_FOUND')
);
const canResumeAnalysis = computed(
  () =>
    !working.value &&
    ['unknown', 'blocked'].includes(operation.value.status) &&
    !operation.value.analysis_id &&
    !operation.value.confirmation_pending &&
    !opportunityMissing.value
);
const canAnalyze = computed(
  () =>
    (operation.value.status === 'draft' || canResumeAnalysis.value) &&
    !opportunityMissing.value &&
    !request.isPending.value &&
    !props.disabled
);
const canConfirm = computed(
  () =>
    ready.value &&
    Boolean(text.value.trim()) &&
    [...text.value.trim()].length <= MAX_TEXT_LENGTH &&
    !request.isPending.value &&
    !props.disabled
);
const stateLabel = computed(() =>
  opportunityMissing.value
    ? t('GG_FOLLOWUP.STATES.INVALID_TARGET')
    : t(`GG_FOLLOWUP.STATES.${operation.value.status.toUpperCase()}`)
);
const canRestart = computed(
  () =>
    contextChanged.value ||
    opportunityMissing.value ||
    operation.value.status === 'failed' ||
    (operation.value.status === 'blocked' &&
      !operation.value.confirmation_pending) ||
    (saved.value && operation.value.note_status === 'done')
);

const stopTimer = () => {
  clearTimeout(timer);
  timer = null;
};
const apply = data => {
  operation.value = data;
  if (!text.value)
    text.value =
      data.texto_confirmado ||
      data.result?.suggestion?.text ||
      data.result?.texto_sugerido ||
      '';
  emit('update', data);
};
const errorMessage = code => {
  const key = `GG_FOLLOWUP.ERRORS.${code}`;
  return te(key)
    ? t(key, { id: operation.value.opportunity_id })
    : t('GG_FOLLOWUP.ERRORS.GATEWAY_FAILED');
};
const showError = exception => {
  const code = exception.response?.data?.code;
  contextChanged.value = code === 'OPPORTUNITY_CHANGED';
  targetRejected.value = code === 'OPPORTUNITY_NOT_FOUND';
  if (code === 'ANALYSIS_REPLACED')
    apply({ ...operation.value, status: 'superseded' });
  error.value = errorMessage(code);
};
const run = async callback => {
  stopTimer();
  error.value = '';
  try {
    const response = await request.run(callback);
    if (response?.data.operation) apply(response.data.operation);
    else if (response) emit('reselect');
  } catch (exception) {
    showError(exception);
  }
};
const refresh = () =>
  run(signal =>
    GgFollowupAPI.operation(props.conversationId, operation.value.request_id, {
      signal,
    })
  );
const analyze = () => {
  if (!canAnalyze.value) return;
  if (support.value) apply({ ...operation.value, status: 'submitting' });
  run(signal =>
    GgFollowupAPI.analyze(props.conversationId, operation.value.request_id, {
      signal,
    })
  );
};
const restart = mode => {
  if (request.isPending.value || operation.value.confirmation_pending) return;
  run(signal =>
    GgFollowupAPI.restart(
      props.conversationId,
      operation.value.request_id,
      mode,
      { signal }
    )
  );
};
const confirm = () => {
  if (!canConfirm.value) return;
  if (props.useTextOnly) {
    emit('useText', {
      requestId: operation.value.request_id,
      ticketId: operation.value.ticket_id,
      text: text.value.trim(),
    });
    return;
  }
  run(signal =>
    GgFollowupAPI.confirm(
      props.conversationId,
      operation.value.request_id,
      text.value,
      { signal }
    )
  );
};
const publishNote = () =>
  run(signal =>
    GgFollowupAPI.note(props.conversationId, operation.value.request_id, {
      signal,
    })
  );

const schedule = () => {
  stopTimer();
  if (!disposed && working.value && !request.isPending.value)
    timer = setTimeout(
      () => refresh(),
      Math.max(POLL_INTERVAL_MS, (operation.value.retry_after || 0) * 1000)
    );
};

watch(() => props.operation, apply, { immediate: true });
watch([working, request.isPending], schedule, { immediate: true });
onUnmounted(() => {
  disposed = true;
  stopTimer();
});
</script>

<template>
  <section class="flex flex-col gap-4">
    <p
      role="status"
      aria-live="polite"
      class="m-0 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-12"
    >
      <Spinner v-if="working" class="me-2 inline-flex" />
      {{ stateLabel }}
    </p>
    <p
      v-if="error || opportunityMissing"
      role="alert"
      class="m-0 text-sm text-n-ruby-11"
    >
      {{ error || errorMessage('OPPORTUNITY_NOT_FOUND') }}
    </p>
    <p class="m-0 text-sm font-medium">
      {{ targetLabel }}
    </p>
    <template v-if="operation.status === 'draft'">
      <p class="m-0 text-xs text-n-slate-11">
        {{
          t('GG_FOLLOWUP.PREVIEW_TARGET', {
            count: operation.payload.selection.message_count,
            target: targetLabel,
          })
        }}
      </p>
      <div
        v-for="message in operation.payload.messages"
        :key="message.id"
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="m-0 text-sm whitespace-pre-wrap break-words">
          {{ message.text }}
        </p>
      </div>
      <p class="m-0 text-xs text-n-slate-11">{{ t('GG_FOLLOWUP.PENDING') }}</p>
      <Button
        type="button"
        :label="t('GG_FOLLOWUP.ANALYZE')"
        :disabled="!canAnalyze"
        :is-loading="request.isPending.value"
        @click="analyze"
      />
      <Button
        type="button"
        :label="
          t(
            opportunityMissing
              ? 'GG_FOLLOWUP.CHANGE_TARGET'
              : 'GG_FOLLOWUP.CHANGE_SELECTION'
          )
        "
        color="slate"
        variant="faded"
        :disabled="request.isPending.value"
        @click="emit('reset')"
      />
    </template>
    <template v-else>
      <p v-if="working" class="m-0 text-sm text-n-slate-11">
        {{ t('GG_FOLLOWUP.BACKGROUND_HINT') }}
      </p>
      <slot v-if="ready" name="review">
        <p class="m-0 text-sm text-n-slate-11">
          {{ t('GG_FOLLOWUP.REVIEW_HINT') }}
        </p>
        <label class="flex flex-col gap-2 text-sm text-n-slate-12">
          {{ t('GG_FOLLOWUP.REVIEW_TEXT') }}
          <textarea
            v-model="text"
            spellcheck="true"
            :lang="locale.replace('_', '-')"
            :readonly="operation.confirmation_pending"
            :maxlength="MAX_TEXT_LENGTH"
            class="w-full min-h-[12rem] rounded-lg border border-n-weak bg-n-background p-3 text-sm"
          />
        </label>
        <div class="flex gap-2">
          <Button
            type="button"
            :label="t('GG_FOLLOWUP.REANALYZE')"
            icon="i-lucide-refresh-cw"
            color="slate"
            variant="faded"
            size="sm"
            class="flex-1"
            :disabled="
              request.isPending.value || operation.confirmation_pending
            "
            @click="restart('reanalyze')"
          />
          <Button
            type="button"
            :label="t('GG_FOLLOWUP.RESELECT')"
            color="slate"
            variant="faded"
            size="sm"
            class="flex-1"
            :disabled="
              request.isPending.value || operation.confirmation_pending
            "
            @click="restart('reselect')"
          />
        </div>
        <p
          v-if="operation.confirmation_pending"
          role="note"
          class="m-0 text-xs text-n-amber-11"
        >
          {{ t('GG_FOLLOWUP.CONFIRMATION_PENDING') }}
        </p>
        <Button
          type="button"
          :label="
            t(
              useTextOnly
                ? 'GG_SUPPORT_STATE.USE_CLOSING_TEXT'
                : operation.confirmation_pending
                  ? 'GG_FOLLOWUP.RETRY_CONFIRM'
                  : 'GG_FOLLOWUP.CONFIRM_SAVE'
            )
          "
          :disabled="!canConfirm"
          :is-loading="request.isPending.value"
          @click="confirm"
        />
      </slot>
      <template v-if="saved">
        <p class="m-0 rounded-lg bg-n-teal-3 p-3 text-sm text-n-teal-11">
          {{
            t(support ? 'GG_FOLLOWUP.SUPPORT.SAVED' : 'GG_FOLLOWUP.SAVED', {
              id: operation.followup_id,
              history: operation.history_id,
              ticket: operation.ticket_id,
              opportunity: operation.opportunity_id,
            })
          }}
        </p>
        <p class="m-0 text-sm whitespace-pre-wrap break-words">
          {{ support ? operation.result.text : operation.result.texto_salvo }}
        </p>
        <p role="status" class="m-0 text-xs text-n-slate-11">
          {{
            t(
              operation.note_status === 'done'
                ? 'GG_FOLLOWUP.NOTE_DONE'
                : 'GG_FOLLOWUP.NOTE_PENDING'
            )
          }}
        </p>
        <Button
          v-if="operation.note_status !== 'done'"
          type="button"
          :label="t('GG_FOLLOWUP.RETRY_NOTE')"
          :disabled="request.isPending.value"
          :is-loading="request.isPending.value"
          @click="publishNote"
        />
      </template>
      <p
        v-if="['failed', 'blocked'].includes(operation.status)"
        role="alert"
        class="m-0 text-sm text-n-ruby-11"
      >
        {{ errorMessage(operation.last_error_code) }}
      </p>
      <Button
        v-if="canResumeAnalysis"
        type="button"
        :label="t('GG_FOLLOWUP.RESUME_ANALYSIS')"
        :disabled="!canAnalyze"
        @click="analyze"
      />
      <Button
        v-if="working || operation.confirmation_pending"
        type="button"
        :label="t('GG_FOLLOWUP.REFRESH_RESULT')"
        color="slate"
        variant="faded"
        :disabled="working || request.isPending.value"
        @click="refresh"
      />
      <Button
        v-if="operation.status === 'superseded'"
        type="button"
        :label="t('GG_FOLLOWUP.RESUME_CURRENT')"
        color="slate"
        variant="faded"
        :disabled="request.isPending.value"
        @click="emit('reload')"
      />
      <Button
        v-if="canRestart"
        type="button"
        :label="
          t(
            opportunityMissing
              ? 'GG_FOLLOWUP.CHANGE_TARGET'
              : 'GG_FOLLOWUP.NEW_ANALYSIS'
          )
        "
        color="slate"
        variant="faded"
        :disabled="request.isPending.value"
        @click="emit('reset')"
      />
    </template>
  </section>
</template>
